import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Map } from "react-map-gl";
import DeckGL from "@deck.gl/react";
import "mapbox-gl/dist/mapbox-gl.css";
import { MAPBOX_TOKEN } from "../../config";

const CompareLayers = ({
    checkingLayers = [],
    viewState,
    onMapClick,
    handleMouseMove,
    setViewState,
    mapStyle,
}) => {
    const containerRef = useRef(null);
    const draggingRef = useRef(false);
    const [pct, setPct] = useState(50);
    const [hover, setHover] = useState(false);

    const leftLayer = useMemo(() => {
        if (!checkingLayers?.[0]) return null;

        return checkingLayers[0].clone({
            id: `${checkingLayers[0].id}-compare-left`,
            visible: true,
            pickable: false,
        });
    }, [checkingLayers]);

    const rightLayer = useMemo(() => {
        if (!checkingLayers?.[1]) return null;

        return checkingLayers[1].clone({
            id: `${checkingLayers[1].id}-compare-right`,
            visible: true,
            pickable: false,
        });
    }, [checkingLayers]);

    const handleStartDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        draggingRef.current = true;
    }, []);

    const handleStopDrag = useCallback(() => {
        draggingRef.current = false;
    }, []);

    const handleDrag = useCallback((e) => {
        if (!draggingRef.current) return;

        const el = containerRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const x = Math.max(rect.left, Math.min(e.clientX, rect.right));

        setPct(((x - rect.left) / rect.width) * 100);
    }, []);

    useEffect(() => {
        window.addEventListener("mousemove", handleDrag);
        window.addEventListener("mouseup", handleStopDrag);

        return () => {
            window.removeEventListener("mousemove", handleDrag);
            window.removeEventListener("mouseup", handleStopDrag);
        };
    }, [handleDrag, handleStopDrag]);

    const handleViewStateChange = ({ viewState: nextViewState }) => {
        setViewState(nextViewState);
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "hidden",
            }}
            onContextMenu={(e) => e.preventDefault()}
        >
            <DeckGL
                layers={leftLayer ? [leftLayer] : []}
                viewState={viewState}
                controller={true}
                onViewStateChange={handleViewStateChange}
                onClick={onMapClick}
                onHover={(info) => {
                    if (!draggingRef.current) {
                        handleMouseMove?.(info);
                    }
                }}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                }}
            >
                <Map
                    mapStyle={mapStyle}
                    mapboxAccessToken={MAPBOX_TOKEN}
                    reuseMaps={false}
                />
            </DeckGL>

            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    pointerEvents: "none",
                    clipPath: `inset(0 0 0 ${pct}%)`,
                    WebkitClipPath: `inset(0 0 0 ${pct}%)`,
                }}
            >
                <DeckGL
                    layers={rightLayer ? [rightLayer] : []}
                    viewState={viewState}
                    controller={false}
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                    }}
                >
                    <Map
                        mapStyle={mapStyle}
                        mapboxAccessToken={MAPBOX_TOKEN}
                        reuseMaps={false}
                    />
                </DeckGL>
            </div>

            <div
                onMouseDown={handleStartDrag}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${pct}%`,
                    transform: "translateX(-50%)",
                    width: "28px",
                    cursor: "ew-resize",
                    zIndex: 50,
                    pointerEvents: "auto",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: hover ? "8px" : "4px",
                        background: "#ffffff",
                        boxShadow: "0 0 8px rgba(0,0,0,0.6)",
                    }}
                />

                <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        background: "#ffffff",
                        color: "#222",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.45)",
                        userSelect: "none",
                    }}
                >
                    ↔
                </div>
            </div>
        </div>
    );
};

export default CompareLayers;